import React, { useState, useEffect } from 'react';
import { API } from '../../App';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import {
  Eye, Palette, Save, FileText, Settings2, ChevronLeft, Search,
  FileSignature, SlidersHorizontal, Building2, Mail, PenLine, Users, CreditCard
} from 'lucide-react';
import ContractTemplateEditor from './ContractTemplateEditor';
import TenantProfileEditor from './TenantProfileEditor';
import EmailSettingsPage from './EmailSettingsPage';
import EmailSignatureEditor from './EmailSignatureEditor';
import OrganizerTeamMembers from './OrganizerTeamMembers';
import BillingSettings from './BillingSettings';

// ─── Sub-views ────────────────────────────────────────────────────────────────

function DisplaySettings() {
  const [companyViewColor, setCompanyViewColor] = useState('#94a3b8');
  const [saving, setSaving] = useState(false);

  const PRESET_COLORS = [
    { label: 'Gri (Nötr)', value: '#94a3b8' },
    { label: 'Lacivert', value: '#1e40af' },
    { label: 'Beyaz', value: '#f8fafc' },
    { label: 'Siyah', value: '#1e293b' },
    { label: 'Mor', value: '#7c3aed' },
    { label: 'Turuncu', value: '#ea580c' },
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/organizer-canvas-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCompanyViewColor(res.data.company_view_color || '#94a3b8'))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/organizer-canvas-settings?company_view_color=${encodeURIComponent(companyViewColor)}`,
        {}, { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ayarlar kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Eye className="w-4 h-4 text-blue-500" /> Müşteri Görünüm Rengi
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          "Müşteri satış statüsünü görmesin" aktifken tüm standlar bu renkte görünür.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {PRESET_COLORS.map(p => (
          <button
            key={p.value}
            onClick={() => setCompanyViewColor(p.value)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border-2 transition-all ${
              companyViewColor === p.value ? 'border-teal-500 font-semibold' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <span className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: p.value }} />
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs text-gray-500">Özel renk</Label>
          <Input type="color" value={companyViewColor} onChange={e => setCompanyViewColor(e.target.value)} className="w-16 h-10 p-1 cursor-pointer" />
        </div>
        <div className="space-y-1 flex-1">
          <Label className="text-xs text-gray-500">Hex kodu</Label>
          <Input value={companyViewColor} onChange={e => setCompanyViewColor(e.target.value)} placeholder="#94a3b8" className="font-mono" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-gray-500">Önizleme</Label>
          <div className="w-16 h-10 rounded border border-gray-300" style={{ backgroundColor: companyViewColor }} />
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
      </Button>
    </div>
  );
}

function ContractSettings() {
  const [settings, setSettings] = useState({
    default_status: 'taslak',
    pdf_export_enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const token = () => localStorage.getItem('token');

  const STATUS_OPTIONS = [
    { value: 'taslak', label: 'Taslak', color: 'bg-gray-100 text-gray-700' },
    { value: 'gönderildi', label: 'Gönderildi', color: 'bg-blue-100 text-blue-700' },
    { value: 'imzalandı', label: 'İmzalandı', color: 'bg-green-100 text-green-700' },
    { value: 'iptal', label: 'İptal', color: 'bg-red-100 text-red-700' },
  ];

  useEffect(() => {
    axios.get(`${API}/contract-settings`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(res => setSettings({ default_status: res.data.default_status || 'taslak', pdf_export_enabled: res.data.pdf_export_enabled !== false }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/contract-settings`, settings, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      toast.success('Sözleşme ayarları kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-6 text-sm text-gray-400">Yükleniyor...</div>;

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-purple-500" /> Sözleşme Ayarları
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Sözleşme oluşturma sürecine ilişkin varsayılan ayarlar.
        </p>
      </div>

      {/* Default Status */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sözleşme Durumu</p>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Varsayılan Durum</p>
              <p className="text-xs text-gray-400 mt-0.5">Yeni oluşturulan sözleşmelerin başlangıç durumu</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setSettings(s => ({ ...s, default_status: opt.value }))}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 transition-all ${
                  settings.default_status === opt.value
                    ? `${opt.color} border-current`
                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
                data-testid={`status-option-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* PDF Export */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dışa Aktarım</p>
        </div>
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">PDF Dışa Aktarım</p>
              <p className="text-xs text-gray-400 mt-0.5">Sözleşmelerde "Yazdır / PDF" butonu gösterilsin</p>
            </div>
            {/* Toggle switch */}
            <button
              onClick={() => setSettings(s => ({ ...s, pdf_export_enabled: !s.pdf_export_enabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.pdf_export_enabled ? 'bg-teal-500' : 'bg-gray-200'
              }`}
              data-testid="pdf-export-toggle"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                settings.pdf_export_enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
            PDF dışa aktarım tarayıcı yazdırma özelliği aracılığıyla çalışır. Ayrı bir yazılım gerektirmez.
          </div>
        </div>
      </div>

      {/* Placeholder info */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dinamik Alanlar</p>
        </div>
        <div className="px-4 py-4">
          <p className="text-sm text-gray-600">Şablonlarda kullanılan dinamik alanları yönetmek için <strong>Şablonlar</strong> sayfasındaki "Dinamik Alanlar" sekmesini kullanın.</p>
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="save-contract-settings-btn">
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
      </Button>
    </div>
  );
}

// ─── Settings Card ─────────────────────────────────────────────────────────────

function SettingsCard({ icon: Icon, iconBg, title, description, onClick, testId }) {
  return (
    <div
      onClick={onClick}
      data-testid={testId}
      className="bg-white border border-gray-200 rounded-xl p-5 cursor-pointer hover:border-teal-300 hover:shadow-md transition-all group"
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${iconBg}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <p className="font-semibold text-gray-800 text-sm group-hover:text-teal-700 transition-colors">{title}</p>
      <p className="text-xs text-gray-400 mt-1 leading-relaxed">{description}</p>
    </div>
  );
}

// ─── Section Divider ───────────────────────────────────────────────────────────

function SectionHeader({ title }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="w-1 h-5 bg-teal-500 rounded-full" />
      <h2 className="text-sm font-semibold text-gray-600 tracking-wide uppercase">{title}</h2>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function OrganizerSettings() {
  const [activeView, setActiveView] = useState(null);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const section = searchParams.get('section');
    if (section) setActiveView(section);
  }, []); // eslint-disable-line

  const SECTIONS = [
    {
      id: 'profil',
      label: 'Organizatör Profili',
      cards: [
        {
          id: 'tenant_profile',
          icon: Building2,
          iconBg: 'bg-teal-600',
          title: 'Firma Profili',
          description: 'Organizatör firma bilgilerinizi (ad, adres, iletişim, yetkili kişiler) yönetin. Sözleşmelerde kullanılır.',
          testId: 'settings-card-tenant-profile',
        },
      ],
    },
    {
      id: 'genel',
      label: 'Genel',
      cards: [
        {
          id: 'display',
          icon: Eye,
          iconBg: 'bg-blue-500',
          title: 'Görünüm Ayarları',
          description: 'Müşteri görünüm rengi ve kroki gizlilik seçeneklerini yapılandırın.',
          testId: 'settings-card-display',
        },
      ],
    },
    {
      id: 'contract_management',
      label: 'Sözleşme Yönetimi',
      cards: [
        {
          id: 'templates',
          icon: FileSignature,
          iconBg: 'bg-teal-500',
          title: 'Şablonlar',
          description: 'Sözleşme şablonları oluşturun, düzenleyin ve yönetin. Dinamik alanlarla özelleştirin.',
          testId: 'settings-card-templates',
        },
        {
          id: 'contract_settings',
          icon: SlidersHorizontal,
          iconBg: 'bg-purple-500',
          title: 'Sözleşme Ayarları',
          description: 'Varsayılan durum, PDF dışa aktarım ve sözleşme süreç ayarlarını yönetin.',
          testId: 'settings-card-contract-settings',
        },
      ],
    },
    {
      id: 'eposta',
      label: 'E-posta',
      cards: [
        {
          id: 'email_settings',
          icon: Mail,
          iconBg: 'bg-blue-500',
          title: 'E-posta Ayarları',
          description: 'SMTP/IMAP sunucu ayarlarını yapılandırın. Kendi e-posta adresinizden müşterilerinize mail gönderin.',
          testId: 'settings-card-email-settings',
        },
        {
          id: 'email_signature',
          icon: PenLine,
          iconBg: 'bg-indigo-500',
          title: 'E-posta İmzası',
          description: 'Profesyonel e-posta imzanızı oluşturun. Ad, unvan, şirket, sosyal medya ve logo ekleyin.',
          testId: 'settings-card-email-signature',
        },
      ],
    },
    {
      id: 'kullanici',
      label: 'Kullanıcılar',
      cards: [
        {
          id: 'user_management',
          icon: Users,
          iconBg: 'bg-teal-600',
          title: 'Kullanıcı Yönetimi',
          description: 'Ekip üyelerini ekleyin, düzenleyin ve yetki düzeylerini yönetin.',
          testId: 'settings-card-user-management',
        },
      ],
    },
    {
      id: 'abonelik',
      label: 'Abonelik & Ödeme',
      cards: [
        {
          id: 'billing',
          icon: CreditCard,
          iconBg: 'bg-emerald-600',
          title: 'Abonelik & Faturalama',
          description: 'Abonelik planınızı yönetin, ödeme geçmişinizi görüntüleyin.',
          testId: 'settings-card-billing',
        },
      ],
    },
  ];

  const allCards = SECTIONS.flatMap(s => s.cards);
  const filteredSections = search.trim()
    ? [{
        id: 'search',
        label: 'Arama Sonuçları',
        cards: allCards.filter(c =>
          c.title.toLowerCase().includes(search.toLowerCase()) ||
          c.description.toLowerCase().includes(search.toLowerCase())
        ),
      }]
    : SECTIONS;

  // Determine which view to render
  const VIEW_MAP = {
    tenant_profile: { title: 'Firma Profili', icon: Building2, component: <TenantProfileEditor /> },
    display: { title: 'Görünüm Ayarları', icon: Eye, component: <DisplaySettings /> },
    templates: { title: 'Şablonlar', icon: FileSignature, component: <ContractTemplateEditor /> },
    contract_settings: { title: 'Sözleşme Ayarları', icon: SlidersHorizontal, component: <ContractSettings /> },
    email_settings: { title: 'E-posta Ayarları', icon: Mail, component: <EmailSettingsPage /> },
    email_signature: { title: 'E-posta İmzası', icon: PenLine, component: <EmailSignatureEditor /> },
    user_management: { title: 'Kullanıcı Yönetimi', icon: Users, component: <OrganizerTeamMembers /> },
    billing: { title: 'Abonelik & Faturalama', icon: CreditCard, component: <BillingSettings /> },
  };

  const currentView = activeView ? VIEW_MAP[activeView] : null;

  return (
    <div className="p-6 max-w-4xl space-y-2">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        {currentView ? (
          <button
            onClick={() => setActiveView(null)}
            className="flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-800 font-medium transition-colors"
            data-testid="settings-back-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Geri
          </button>
        ) : null}
        <div className="flex items-center gap-2">
          <Settings2 className={`w-6 h-6 ${currentView ? 'text-gray-400' : 'text-teal-600'}`} />
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {currentView ? currentView.title : 'Ayarlar'}
            </h1>
            {!currentView && (
              <p className="text-xs text-gray-400">Sistem ayarlarını yönetin</p>
            )}
          </div>
        </div>
      </div>

      {/* Sub-view content */}
      {currentView ? (
        <div className="pt-4">{currentView.component}</div>
      ) : (
        <>
          {/* Search */}
          <div className="relative max-w-sm pb-2">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Ayarlarda ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="settings-search"
            />
          </div>

          {/* Sections + Cards */}
          <div className="space-y-6 pt-2">
            {filteredSections.map(section => (
              <div key={section.id} className="space-y-3">
                <SectionHeader title={section.label} />
                {section.cards.length === 0 ? (
                  <p className="text-sm text-gray-400 pl-4">Sonuç bulunamadı</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {section.cards.map(card => (
                      <SettingsCard
                        key={card.id}
                        icon={card.icon}
                        iconBg={card.iconBg}
                        title={card.title}
                        description={card.description}
                        testId={card.testId}
                        onClick={() => setActiveView(card.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
